/**
 * NotasContables — Ajustes manuales auditados (crédito/débito) por agencia.
 *
 * Reglas de impacto:
 *   Crédito + CxC  →  reduce lo que la agencia debe (Entretur perdona)
 *   Crédito + CxP  →  aumenta lo que Entretur debe a la agencia
 *   Débito  + CxC  →  aumenta lo que la agencia debe a Entretur
 *   Débito  + CxP  →  reduce lo que Entretur debe a la agencia
 *
 * Las notas aparecen automáticamente como movimientos en el Estado de cuenta.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Autocomplete, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Tooltip, IconButton, MenuItem, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || u.company_id || u.empresa_id || '1';
  } catch { return '1'; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
}
function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Visual por combinación tipo × afecta — color/etiqueta consistentes con
// Estado de cuenta. La idea es que el operador identifique de un vistazo
// si la nota "alivia" o "carga" a la agencia.
function visualNota(tipo, afecta) {
  if (tipo === 'credito' && afecta === 'cxc') return { label: 'Crédito CxC', color: '#16a34a', bg: '#dcfce7', desc: 'Reduce deuda de la agencia' };
  if (tipo === 'credito' && afecta === 'cxp') return { label: 'Crédito CxP', color: '#f59e0b', bg: '#fef3c7', desc: 'Aumenta lo que Entretur debe' };
  if (tipo === 'debito'  && afecta === 'cxc') return { label: 'Débito CxC',  color: '#dc2626', bg: '#fee2e2', desc: 'Aumenta deuda de la agencia' };
  return { label: 'Débito CxP', color: '#0ea5e9', bg: '#e0f2fe', desc: 'Reduce lo que Entretur debe' };
}


export default function NotasContables() {
  const navigate = useNavigate();
  const empresaId = getEmpresaId();
  const [searchParams] = useSearchParams();
  const agenciaPreId = searchParams.get('agencia');

  const [notas, setNotas] = useState([]);
  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroAgencia, setFiltroAgencia] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroAfecta, setFiltroAfecta] = useState('');
  const [incluirAnuladas, setIncluirAnuladas] = useState(false);
  const [dialogNueva, setDialogNueva] = useState(false);

  // Cargar lista de agencias para el autocomplete del filtro
  useEffect(() => {
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setAgencias(Array.isArray(d) ? d : (d.agencias || [])))
      .catch(() => setAgencias([]));
  }, [empresaId]);

  // Pre-seleccionar agencia si vino en query (deep-link desde Estado de cuenta)
  useEffect(() => {
    if (agenciaPreId && agencias.length > 0 && !filtroAgencia) {
      const ag = agencias.find(a => String(a.id) === String(agenciaPreId));
      if (ag) setFiltroAgencia(ag);
    }
  }, [agenciaPreId, agencias, filtroAgencia]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId });
      if (filtroAgencia) params.append('agencia_id', filtroAgencia.id);
      if (filtroTipo) params.append('tipo', filtroTipo);
      if (filtroAfecta) params.append('afecta', filtroAfecta);
      if (incluirAnuladas) params.append('incluir_anuladas', 'true');
      const r = await fetch(`${API_BASE}/turismo/finanzas/notas-contables?${params}`,
        { headers: getAuthHeaders() });
      setNotas(await r.json());
    } catch { setNotas([]); }
    finally { setLoading(false); }
  }, [empresaId, filtroAgencia, filtroTipo, filtroAfecta, incluirAnuladas]);

  useEffect(() => { cargar(); }, [cargar]);

  const anular = async (nota) => {
    // eslint-disable-next-line no-alert
    const motivo = window.prompt(
      `Anular nota NC-${nota.id} (${visualNota(nota.tipo, nota.afecta).label}) por ${fmtMoney(nota.monto)}?\n\n` +
      'La nota queda en histórico pero deja de impactar los saldos. Motivo:'
    );
    if (!motivo || motivo.trim().length < 3) return;
    const user = getUser();
    const res = await fetch(`${API_BASE}/turismo/finanzas/notas-contables/${nota.id}/anular`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({
        motivo,
        anulada_por_user_id: user.id || user.user_id || null,
        anulada_por_nombre: user.name || null,
      }),
    });
    if (res.ok) {
      cargar();
    } else {
      const e = await res.json();
      // eslint-disable-next-line no-alert
      alert(e.detail || 'Error anulando la nota');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#134e4a', mb: 0.5 }}>
            Notas contables
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Ajustes manuales auditados sobre cuentas por cobrar y por pagar.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setDialogNueva(true)}
          sx={{ textTransform: 'none', bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}>
          Nueva nota
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0',
          display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <Autocomplete sx={{ minWidth: 240 }} size="small"
          options={agencias}
          getOptionLabel={(o) => o.nombre || ''}
          value={filtroAgencia}
          onChange={(_, v) => setFiltroAgencia(v)}
          renderInput={(params) => <TextField {...params} label="Agencia" placeholder="Todas" />}
        />
        <TextField select size="small" label="Tipo" value={filtroTipo}
          onChange={e => setFiltroTipo(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="credito">Crédito</MenuItem>
          <MenuItem value="debito">Débito</MenuItem>
        </TextField>
        <TextField select size="small" label="Afecta" value={filtroAfecta}
          onChange={e => setFiltroAfecta(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Ambos</MenuItem>
          <MenuItem value="cxc">CxC</MenuItem>
          <MenuItem value="cxp">CxP</MenuItem>
        </TextField>
        <Button size="small" onClick={() => setIncluirAnuladas(!incluirAnuladas)}
          variant={incluirAnuladas ? 'contained' : 'outlined'}
          sx={{ textTransform: 'none', fontSize: 11 }}>
          {incluirAnuladas ? 'Ocultar anuladas' : 'Incluir anuladas'}
        </Button>
        <IconButton onClick={cargar} sx={{ bgcolor: '#f1f5f9' }}>
          <RefreshIcon />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>{notas.length} notas</Typography>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: '#0d9488' }} />
        </Box>
      )}

      {!loading && notas.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <Typography sx={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
            No hay notas contables registradas con esos filtros.
          </Typography>
        </Paper>
      )}

      {!loading && notas.length > 0 && (
        <TableContainer component={Paper} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Tipo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Monto</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Concepto</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Reserva</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Creada por</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Estado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notas.map(n => {
                const v = visualNota(n.tipo, n.afecta);
                return (
                  <TableRow key={n.id} hover sx={{ opacity: n.anulada ? 0.5 : 1 }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>NC-{n.id}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{fmtFecha(n.fecha_emision)}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{n.agencia_nombre}</TableCell>
                    <TableCell>
                      <Tooltip title={v.desc}>
                        <Chip size="small" label={v.label}
                          sx={{ bgcolor: v.bg, color: v.color, fontSize: 9.5, fontWeight: 700, height: 18 }} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: v.color }}>
                      {fmtMoney(n.monto)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>
                      {n.concepto}
                      {n.motivo_detallado && (
                        <Typography sx={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic', mt: 0.25 }}>
                          {n.motivo_detallado.length > 60
                            ? n.motivo_detallado.slice(0, 60) + '…'
                            : n.motivo_detallado}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 10, fontFamily: 'monospace', color: '#64748b' }}>
                      {n.reservacion_codigo || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{n.creado_por_nombre || '—'}</TableCell>
                    <TableCell align="center">
                      {n.anulada ? (
                        <Tooltip title={`Anulada: ${n.motivo_anulacion || ''} (${fmtFecha(n.anulada_en)})`}>
                          <Chip size="small" label="Anulada"
                            sx={{ bgcolor: '#fee2e2', color: '#991b1b', height: 18, fontSize: 10 }} />
                        </Tooltip>
                      ) : (
                        <Chip size="small" label="Vigente"
                          sx={{ bgcolor: '#dcfce7', color: '#166534', height: 18, fontSize: 10 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Abrir estado de cuenta">
                        <IconButton size="small"
                          onClick={() => navigate(`/finanzas/estado-cuenta?agencia=${n.agencia_id}`)}>
                          <OpenInNewIcon fontSize="small" sx={{ color: '#64748b' }} />
                        </IconButton>
                      </Tooltip>
                      {!n.anulada && (
                        <Tooltip title="Anular nota">
                          <IconButton size="small" onClick={() => anular(n)}>
                            <UndoIcon fontSize="small" sx={{ color: '#dc2626' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DialogNuevaNota
        open={dialogNueva}
        agencias={agencias}
        agenciaPre={filtroAgencia}
        onClose={() => setDialogNueva(false)}
        onSaved={() => { setDialogNueva(false); cargar(); }}
      />
    </Box>
  );
}


// ============================================================================
// DIÁLOGO: Nueva nota
// ============================================================================

function DialogNuevaNota({ open, agencias, agenciaPre, onClose, onSaved }) {
  const empresaId = getEmpresaId();
  const user = getUser();
  const [agencia, setAgencia] = useState(null);
  const [tipo, setTipo] = useState('credito');
  const [afecta, setAfecta] = useState('cxc');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState(todayISO());
  const [concepto, setConcepto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [codigoReserva, setCodigoReserva] = useState('');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (open) {
      setAgencia(agenciaPre || null);
      setTipo('credito');
      setAfecta('cxc');
      setMonto('');
      setFecha(todayISO());
      setConcepto('');
      setMotivo('');
      setCodigoReserva('');
      setError(null);
    }
  }, [open, agenciaPre]);

  const v = visualNota(tipo, afecta);

  const guardar = async () => {
    if (!agencia) { setError('Seleccioná una agencia'); return; }
    const m = Number(monto);
    if (!m || m <= 0) { setError('Ingresá un monto válido'); return; }
    if (!concepto || concepto.trim().length < 3) { setError('Ingresá un concepto (min 3 caracteres)'); return; }

    // Resolver reservacion_id si se ingresó un código
    let reservacion_id = null;
    if (codigoReserva && codigoReserva.trim()) {
      try {
        const r = await fetch(
          `${API_BASE}/turismo/reservaciones?empresa_id=${empresaId}&codigo=${encodeURIComponent(codigoReserva.trim())}`,
          { headers: getAuthHeaders() }
        );
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.reservaciones || []);
        const match = list.find(x => x.codigo_reserva === codigoReserva.trim());
        if (match) reservacion_id = match.id;
        else {
          setError(`No se encontró la reserva ${codigoReserva}`);
          return;
        }
      } catch {
        setError('Error buscando la reserva');
        return;
      }
    }

    setGuardando(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/turismo/finanzas/notas-contables`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          empresa_id: empresaId,
          agencia_id: agencia.id,
          tipo, afecta,
          monto: m,
          fecha_emision: fecha,
          concepto: concepto.trim(),
          motivo_detallado: motivo.trim() || null,
          reservacion_id,
          creado_por_user_id: user.id || user.user_id || null,
          creado_por_nombre: user.name || null,
          creado_por_email: user.email || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Error guardando la nota');
      } else {
        onSaved?.();
      }
    } catch {
      setError('Error de red');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#0d9488', color: '#fff', py: 1.5 }}>
        Nueva nota contable
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Autocomplete size="small" options={agencias}
            getOptionLabel={(o) => o.nombre || ''}
            value={agencia} onChange={(_, v) => setAgencia(v)}
            renderInput={(params) => <TextField {...params} label="Agencia" required />}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
            <TextField select size="small" label="Tipo" value={tipo} onChange={e => setTipo(e.target.value)} required>
              <MenuItem value="credito">Crédito</MenuItem>
              <MenuItem value="debito">Débito</MenuItem>
            </TextField>
            <TextField select size="small" label="Afecta" value={afecta} onChange={e => setAfecta(e.target.value)} required>
              <MenuItem value="cxc">CxC (cuenta x cobrar)</MenuItem>
              <MenuItem value="cxp">CxP (cuenta x pagar)</MenuItem>
            </TextField>
          </Box>

          {/* Hint visual del impacto */}
          <Alert severity="info" sx={{
            fontSize: 11, py: 0.5, bgcolor: v.bg, color: v.color, border: `1px solid ${v.color}40`,
            '& .MuiAlert-icon': { color: v.color },
          }}>
            <b>{v.label}:</b> {v.desc}
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
            <TextField size="small" type="number" label="Monto" value={monto}
              onChange={e => setMonto(e.target.value)} required inputProps={{ min: 0, step: 1000 }} />
            <TextField size="small" type="date" label="Fecha emisión"
              InputLabelProps={{ shrink: true }} value={fecha} onChange={e => setFecha(e.target.value)} required />
          </Box>

          <TextField size="small" label="Concepto" value={concepto}
            onChange={e => setConcepto(e.target.value)} required
            placeholder="ej: Bonificación por volumen mensual"
            inputProps={{ maxLength: 200 }} />

          <TextField size="small" label="Motivo detallado (opcional)" multiline minRows={2} maxRows={4}
            value={motivo} onChange={e => setMotivo(e.target.value)}
            placeholder="Explicación larga del ajuste para auditoría" />

          <TextField size="small" label="Código de reserva (opcional)" value={codigoReserva}
            onChange={e => setCodigoReserva(e.target.value)}
            placeholder="ENT-20260619-001"
            helperText="Vinculá la nota a una reserva específica si aplica" />

          {error && <Alert severity="error" sx={{ fontSize: 12 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button onClick={guardar} variant="contained" disabled={guardando}
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{ textTransform: 'none', bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}>
          {guardando ? 'Guardando…' : 'Crear nota'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
